import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import Button from '@/shared/components/Button';
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import SimpleDatePicker from '@/shared/components/input/SimpleDatePicker';
import TimeInput from '@/shared/components/input/TimeInput';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import api from '@/shared/api';
import handleHttpError from '@/shared/services/HandleHttpError';
import { minutesToTimeSpan } from '@/shared/utils/adminpulse';
import dateFormatService from '@/shared/services/DateFormatService';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import messages from '../employeeDetailMessages';
const formatMinutes = (m) => minutesToTimeSpan(m || 0);
const EmployeeOvertimeModal = ({ show, onClose, onOvertimeChanged, employee, }) => {
    const intl = useIntl();
    const employeeId = employee === null || employee === void 0 ? void 0 : employee.employeeID;
    const canEdit = useMemo(() => AuthService.hasPermission({
        functionality: functionalities.AbsenceAndCounters,
        privilege: privileges.Update,
    }), []);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState([]);
    const [overtimeRemaining, setOvertimeRemaining] = useState(0);
    const [factor, setFactor] = useState(1);
    const [correction, setCorrection] = useState(null);
    const [date, setDate] = useState(moment()
        .utc()
        .startOf('day'));
    const [remark, setRemark] = useState('');
    const reloadData = async () => {
        const { data } = await api.base.get(`/api/employee/OvertimeCorrection/${employeeId}`);
        setRows((data || [])
            .map((row) => (Object.assign(Object.assign({}, row), { date: moment.utc(row.date) })))
            .sort(dynamicSort('-date')));
    };
    const refreshRemaining = async () => {
        const { data } = await api.base.get(`/api/employee/OvertimeRemaining/${employeeId}`);
        setOvertimeRemaining(data);
        setFactor(data > 0 ? -1 : 1);
        return data;
    };
    useEffect(() => {
        if (!show || !employeeId)
            return;
        setLoading(true);
        setCorrection(null);
        setRemark('');
        setDate(moment()
            .utc()
            .startOf('day'));
        Promise.all([reloadData(), refreshRemaining()])
            .catch(error => handleHttpError(intl)(error))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, employeeId]);
    const addRow = async () => {
        if (!correction || saving)
            return;
        setSaving(true);
        try {
            await api.base.post('/api/employee/OvertimeCorrection', {
                employeeId,
                date: date.toISOString(),
                correction: correction * factor,
                remark,
            });
            const updated = await refreshRemaining();
            onOvertimeChanged(updated);
            onClose();
        }
        catch (error) {
            handleHttpError(intl)(error);
        }
        finally {
            setSaving(false);
        }
    };
    const deleteRow = async (row) => {
        setSaving(true);
        try {
            await api.base.delete(`/api/employee/OvertimeCorrection/${row.id}`);
            const [, updated] = await Promise.all([reloadData(), refreshRemaining()]);
            onOvertimeChanged(updated);
        }
        catch (error) {
            handleHttpError(intl)(error);
        }
        finally {
            setSaving(false);
        }
    };
    const auditTitle = (row) => [
        `${intl.formatMessage(messages.CreatedOn)}: ${dateFormatService.formatDateWithTime(row.createDate)}`,
        `${intl.formatMessage(messages.By)}: ${row.createdBy}`,
        `${intl.formatMessage(messages.PreviousRemainingOvertime)}: ${formatMinutes(row.previousRemainingOvertime)}`,
    ].join('\n');
    return (_jsxs(Modal, Object.assign({ show: show, onHide: onClose, dialogClassName: "modal-lg", backdrop: "static" }, { children: [_jsx(Modal.Header, Object.assign({ closeButton: true }, { children: _jsxs(Modal.Title, { children: [_jsx("span", { children: intl.formatMessage(messages.OvertimeCorrections) }), _jsx(ZendeskHelp, { article: "5434842124946", className: "margin5L" })] }) })), _jsx(Modal.Body, { children: _jsxs(LoadingContainer, Object.assign({ isLoading: loading }, { children: [_jsxs("table", Object.assign({ className: "table table-condensed table-hover" }, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: intl.formatMessage(messages.Date) }), _jsx("th", Object.assign({ colSpan: 2 }, { children: intl.formatMessage(messages.Correction) })), _jsx("th", Object.assign({ className: "remarkModal-column" }, { children: intl.formatMessage(messages.Remark) })), _jsx("th", {})] }) }), _jsx("tbody", { children: rows.map(row => (_jsxs("tr", { children: [_jsx("td", { children: dateFormatService.formatDate(row.date) }), _jsx("td", Object.assign({ colSpan: 2 }, { children: formatMinutes(row.correction) })), _jsx("td", Object.assign({ className: "remarkModal-column" }, { children: row.remark })), _jsxs("td", Object.assign({ className: "text-right" }, { children: [_jsx("span", Object.assign({ className: "margin10R", title: auditTitle(row) }, { children: _jsx("i", { className: "fa fa-info-circle fa-fw" }) })), canEdit && (_jsx("button", Object.assign({ type: "button", className: "btn btn-default btn-sm", disabled: saving, onClick: () => deleteRow(row) }, { children: _jsx("i", { className: "fa fa-trash" }) })))] }))] }, row.id))) }), canEdit && (_jsx("tfoot", { children: _jsxs("tr", { children: [_jsx("td", { children: _jsx(SimpleDatePicker, { id: "overtimeDate", date: date, onDateChange: (d) => setDate(d ||
                                                        moment()
                                                            .utc()
                                                            .startOf('day')), disabled: saving, isOutsideRange: () => false, numberOfMonths: 1, small: true }) }), _jsx("td", Object.assign({ style: { width: 150 } }, { children: _jsx(TimeInput, { id: "overtimeCorrection", value: correction || 0, onChange: (val) => setCorrection(val), min: 0, max: 2147483647, disabled: saving }) })), _jsx("td", { children: _jsxs("select", Object.assign({ className: "form-control input-sm", value: factor, onChange: e => setFactor(parseInt(e.target.value, 10)), disabled: saving }, { children: [_jsx("option", Object.assign({ value: -1 }, { children: intl.formatMessage(messages.SubtractTime) })), _jsx("option", Object.assign({ value: 1 }, { children: intl.formatMessage(messages.AddTime) }))] })) }), _jsx("td", { children: _jsx("textarea", { className: "form-control vertical-resize remarkModal-column", placeholder: intl.formatMessage(messages.Remark), value: remark, onChange: e => setRemark(e.target.value), maxLength: 3000 }) }), _jsx("td", { className: "text-right" })] }) }))] })), _jsxs("div", Object.assign({ className: "row" }, { children: [_jsxs("div", Object.assign({ className: "col-sm-6" }, { children: [_jsxs("label", { children: [_jsx("span", { children: intl.formatMessage(messages.RemainingOvertimeHours) }), ":", ' '] }), _jsxs("span", Object.assign({ className: "bold" }, { children: [" ", formatMinutes(overtimeRemaining)] }))] })), !!correction && (_jsxs("div", Object.assign({ className: "col-sm-6" }, { children: [_jsxs("label", { children: [_jsx("span", { children: intl.formatMessage(messages.RemainingOvertimeHoursAfterCorrection) }), ":", ' '] }), _jsxs("span", Object.assign({ className: "bold" }, { children: [' ', formatMinutes(overtimeRemaining + correction * factor)] }))] })))] }))] })) }), _jsxs(Modal.Footer, { children: [_jsx(Button, Object.assign({ onClick: onClose, disabled: saving }, { children: intl.formatMessage(messages.Close) })), canEdit && (_jsx(Button, Object.assign({ onClick: addRow, disabled: saving || !correction, btnClassName: "btn-inverse", showProgress: saving }, { children: intl.formatMessage(messages.Save) })))] })] })));
};
export default EmployeeOvertimeModal;
